import { ResponseType } from '@amzn/gbsa-helios-typescript-client';

/**
 * Enhanced Response Types (ERT) classification — the single source of
 * truth for "is this sub-question a data-collection field?".
 *
 * A sub-question's `responseType` is either:
 *   - absent / `PASS_FAIL` → the legacy, SCORED Pass/Fail toggle, or
 *   - one of TEXT / NUMERIC / SINGLE_SELECT / MULTI_SELECT / YES_NO /
 *     USER_ALIAS → a DATA-COLLECTION field that captures a value and
 *     is NOT scored.
 *
 * Every branch in the perform-audit write path, status derivation,
 * and render layer that must distinguish the two flows routes through
 * this predicate so the "undefined means PASS_FAIL" rule lives in one
 * place. A missing `responseType` MUST be treated as PASS_FAIL for
 * backward compatibility — every audit answered before ERT existed
 * carries no `responseType`, and misclassifying those as
 * data-collection would break existing scored audits.
 *
 * The type predicate narrows to
 * `Exclude<ResponseType, typeof ResponseType.PASS_FAIL>` so the
 * false branch covers BOTH `undefined` AND `PASS_FAIL` — the only
 * two non-data-collection states. The previous `responseType is
 * ResponseType` predicate was unsound: TypeScript would narrow the
 * false branch to just `undefined`, making `PASS_FAIL` appear
 * reachable as a data-collection type in some static-analysis paths.
 *
 * Pure — no react, no side effects.
 */
export function isDataCollectionType(
  responseType: ResponseType | undefined,
): responseType is Exclude<ResponseType, typeof ResponseType.PASS_FAIL> {
  return responseType !== undefined && responseType !== ResponseType.PASS_FAIL;
}
